// CustomerFAQ.js
// Public FAQ page for Evermark Memorial customers.
// Organized into collapsible accordion sections — only one item open at a time.
// Route: /faq (public, no auth required)

import React, { useState } from 'react';

// ===== FAQ DATA =====
// Each section has a title and an array of { question, answer } pairs.
const FAQ_SECTIONS = [
  {
    section: 'Client Questions',
    items: [
      {
        question: 'Why choose Evermark Memorial?',
        answer: 'We understand that visiting and maintaining a loved one\'s resting place is deeply personal. Our goal is to provide dependable, respectful care for families who may live far away, have physical limitations, busy schedules, or simply want peace of mind knowing someone is checking in on their loved one\'s memorial with care and compassion.',
      },
      {
        question: 'How quickly can service be scheduled?',
        answer: 'Scheduling depends on cemetery location, weather conditions, and seasonal demand. We always do our best to accommodate requests as quickly as possible, especially around holidays, anniversaries, and Memorial Day.',
      },
      {
        question: 'What cemeteries do you work with?',
        answer: 'We currently provide services throughout Mahoning County, Trumbull County, and Lawrence County cemeteries. If you are unsure whether your cemetery is within our service area, feel free to contact us and we will gladly check.',
      },
      {
        question: 'What happens if a headstone is damaged?',
        answer: 'Our caretakers use gentle, respectful cleaning methods designed to avoid damage. If we notice existing damage, instability, or concerns at the gravesite, we will document it and notify the family. We do not perform repairs or restoration work unless specifically partnered with a professional restoration provider.',
      },
      {
        question: 'Can I purchase memorial care as a gift?',
        answer: 'Yes. Memorial care visits can be gifted to family members or loved ones who may appreciate help maintaining a gravesite from afar or during difficult times.',
      },
      {
        question: 'How do seasonal decoration plans work?',
        answer: 'Seasonal plans allow families to schedule recurring decoration visits throughout the year. Decorations may include spring florals, Memorial Day arrangements, autumn décor, winter greenery, holiday pieces, birthday tributes, or custom themes requested by the family.',
      },
    ],
  },
  {
    section: 'General Service',
    items: [
      {
        question: 'What services do you provide?',
        answer: 'We provide gravesite cleaning, flower placement, seasonal decorations, memorial visits, photo updates, and ongoing gravesite care for loved ones.',
      },
      {
        question: 'Who typically uses your service?',
        answer: 'Families who live out of town, elderly family members, busy professionals, or anyone who wants extra help caring for a loved one\'s resting place.',
      },
      {
        question: 'What areas do you currently serve?',
        answer: 'We currently serve cemeteries throughout Mahoning and Trumbull County, Ohio and Lawrence County, Pennsylvania, with plans to expand in the future.',
      },
      {
        question: 'How do I request service?',
        answer: 'You can submit a request through our website.',
      },
    ],
  },
  {
    section: 'Cleaning & Care',
    items: [
      {
        question: 'What does a standard gravesite cleaning include?',
        answer: 'Typical cleanings may include: Removing dirt and debris, Gentle stone washing, Weed removal around the marker, Straightening flowers or décor, Fresh photo updates after service.',
      },
      {
        question: 'Do you use safe cleaning products?',
        answer: 'Yes. We use gentle, stone-safe methods designed to avoid damaging headstones or memorial markers.',
      },
      {
        question: 'Can you decorate gravesites for holidays or birthdays?',
        answer: 'Absolutely. We offer seasonal and custom memorial decorations for holidays, anniversaries, birthdays, Memorial Day, and more.',
      },
      {
        question: 'Can I send my own decorations or flowers?',
        answer: 'Yes. Families can ship or drop off approved items for placement when arranged in advance.',
      },
    ],
  },
  {
    section: 'Booking & Pricing',
    items: [
      {
        question: 'Do you offer one-time visits or recurring plans?',
        answer: 'Both. We offer single visits as well as monthly or seasonal care plans.',
      },
      {
        question: 'How much does service cost?',
        answer: 'Pricing varies based on cemetery location, plot condition, requested services, and frequency of visits. View our pricing page for details.',
      },
      {
        question: 'Will I receive updates after the visit?',
        answer: 'Yes. We provide photo updates so families can see the completed care and memorial setup.',
      },
      {
        question: 'What if weather affects the appointment?',
        answer: 'If severe weather prevents safe service, we will reschedule as soon as possible and notify you.',
      },
    ],
  },
  {
    section: 'Trust & Safety',
    items: [
      {
        question: 'How do I know the visit was completed?',
        answer: 'Every completed visit includes photo documentation so families can see the gravesite after care has been provided.',
      },
      {
        question: 'What makes your service different from simply hiring a landscaper?',
        answer: 'Our focus is memorial care, not general lawn work. We approach every gravesite with respect, compassion, and attention to the emotional importance behind each visit.',
      },
      {
        question: 'Are decorations cemetery-approved?',
        answer: 'We do our best to follow cemetery guidelines regarding placement and seasonal decorations. Rules can vary by location, and some items may need adjusted or removed depending on cemetery policies.',
      },
      {
        question: 'Can I request a completely custom memorial setup?',
        answer: 'Yes. We welcome meaningful personal touches whenever possible and will work with families to create respectful custom arrangements or requests.',
      },
      {
        question: 'Are your caretakers vetted?',
        answer: 'Yes. All caretakers go through a screening and approval process before representing Evermark Memorial.',
      },
      {
        question: 'Will caretakers respect cemetery rules?',
        answer: 'Absolutely. We follow cemetery guidelines and always maintain a respectful presence.',
      },
      {
        question: 'Are you insured?',
        answer: 'We are currently operating with appropriate business protections and are continuing to expand our coverage as we grow.',
      },
    ],
  },
];

// ===== ACCORDION ITEM =====
// Renders a single question/answer pair.
// openKey and setOpenKey are lifted up so only one item is open at a time.
function AccordionItem({ question, answer, itemKey, openKey, setOpenKey }) {
  const isOpen = openKey === itemKey;

  const toggle = () => {
    // Close if already open, otherwise open this one
    setOpenKey(isOpen ? null : itemKey);
  };

  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <button className="faq-question" onClick={toggle} aria-expanded={isOpen}>
        <span>{question}</span>
        {/* Chevron rotates 180° when open */}
        <span className={`faq-chevron${isOpen ? ' faq-chevron--open' : ''}`}>
          &#8964;
        </span>
      </button>

      {/* Answer panel — height animates via max-height trick */}
      <div className={`faq-answer${isOpen ? ' faq-answer--open' : ''}`}>
        <p>{answer}</p>
      </div>
    </div>
  );
}

// ===== MAIN COMPONENT =====
function CustomerFAQ() {
  // Tracks which accordion item is open — null means all closed.
  // Key format: "sectionIndex-itemIndex" so it's globally unique.
  const [openKey, setOpenKey] = useState(null);

  return (
    <div className="faq-page">

      {/* ===== NAVBAR ===== */}
      <nav className="faq-nav">
        <a href="/" className="faq-nav-logo">Evermark Memorial</a>
        <div className="faq-nav-links">
          <a href="/signup" className="faq-nav-cta">Book a Visit</a>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <div className="faq-hero">
        <span className="faq-hero-label">Support</span>
        <h1 className="faq-hero-title">Frequently Asked Questions</h1>
        <p className="faq-hero-subtitle">
          Everything you need to know about Evermark Memorial's grave care services.
        </p>
        {/* Tab toggle — links to the caretaker FAQ */}
        <div className="faq-toggle">
          <span className="faq-toggle-btn faq-toggle-btn--active">For Families</span>
          <a href="/caretaker-faq" className="faq-toggle-btn">For Caretakers</a>
        </div>
      </div>

      {/* ===== ACCORDION SECTIONS ===== */}
      <div className="faq-content">
        {FAQ_SECTIONS.map((section, sIdx) => (
          <div key={sIdx} className="faq-section">

            {/* Section heading in brand green */}
            <h2 className="faq-section-title">{section.section}</h2>

            {/* Accordion items for this section */}
            <div className="faq-section-items">
              {section.items.map((item, iIdx) => (
                <AccordionItem
                  key={iIdx}
                  itemKey={`${sIdx}-${iIdx}`}
                  question={item.question}
                  answer={item.answer}
                  openKey={openKey}
                  setOpenKey={setOpenKey}
                />
              ))}
            </div>

          </div>
        ))}
      </div>

      {/* ===== BOTTOM CTA ===== */}
      <div className="faq-cta-block">
        <h3>Still have questions?</h3>
        <p>Our team is here to help. Reach out and we'll get back to you quickly.</p>
        <a href="/contact">
          <button className="btn-primary">Contact Us</button>
        </a>
      </div>

      {/* ===== FOOTER ===== */}
      <footer className="faq-footer">
        <p>© 2026 Evermark Memorial. All rights reserved.</p>
        <div className="faq-footer-links">
          <a href="/terms">Terms of Service</a>
          <a href="/privacy">Privacy Policy</a>
          <a href="/contact">Contact</a>
        </div>
      </footer>

    </div>
  );
}

export default CustomerFAQ;
